// Auto-Reorder Önerileri — min_stock altına düşen stoklar + son tedarikçi + öneri miktarı.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, ListPageHeader, EmptyState } from '../components/ui';
import { toast } from '../components/Toast';
import { fmtAmount, fmtQty } from '../utils/numberFormat';

const STATE_CONFIG = {
  out:      { label: 'Tükendi',    color: '#dc2626', bg: 'var(--danger-bg)' },
  critical: { label: 'Kritik',     color: '#f59e0b', bg: 'var(--warning-bg)' },
  reorder:  { label: 'Sipariş Et', color: '#3b82f6', bg: 'var(--info-bg)' },
};

export default function ReorderSuggestionsPage({ onNavigate }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState({});

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/purchase-insights/reorder-suggestions');
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = filter === 'all' ? list : list.filter(it => it.state === filter);
  const counts = {
    all: list.length,
    out: list.filter(i => i.state === 'out').length,
    critical: list.filter(i => i.state === 'critical').length,
    reorder: list.filter(i => i.state === 'reorder').length,
  };

  const toggleSelect = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const totalSuggested = list.filter(i => selected[i.id])
    .reduce((a, b) => a + (parseFloat(b.suggested_qty) * parseFloat(b.last_unit_price) || 0), 0);

  // Seçilen kalemleri tedarikçiye göre grupla — her tedarikçi için ayrı PO önerisi
  const createBulkPOs = async () => {
    const selectedItems = list.filter(i => selected[i.id] && i.last_account_id);
    const withoutVendor = list.filter(i => selected[i.id] && !i.last_account_id);
    if (withoutVendor.length) {
      toast.error(`${withoutVendor.length} ürünün son tedarikçisi yok — atlandı`);
    }
    if (!selectedItems.length) {
      toast.error('Tedarikçisi olan kalem seçmeniz lazım');
      return;
    }

    // Tedarikçi başına grupla
    const groupMap = {};
    for (const it of selectedItems) {
      const vid = it.last_account_id;
      if (!groupMap[vid]) groupMap[vid] = { account_id: vid, vendor_name: it.last_vendor_title, items: [] };
      groupMap[vid].items.push({
        stock_id: it.id,
        quantity: parseFloat(it.suggested_qty) || 1,
        unit_price: parseFloat(it.last_unit_price) || 0,
        description: it.name,
      });
    }
    const groups = Object.values(groupMap);
    if (!window.confirm(`${groups.length} tedarikçiye ${selectedItems.length} kalem için ayrı sipariş oluşturulacak. Devam?`)) return;

    try {
      const r = await api.post('/purchase-insights/reorder/create-orders', { groups });
      if (r.data?.success) {
        const created = r.data.data.created || [];
        const failed = r.data.data.failed || [];
        if (created.length) toast.success(`${created.length} sipariş oluşturuldu: ${created.map(c => c.document_number).join(', ').substring(0, 80)}`);
        if (failed.length) {
          const failMsg = failed.map(f => {
            const vendor = groupMap[f.account_id]?.vendor_name || f.account_id;
            return `${vendor}: ${f.message}`;
          }).join('\n');
          toast.error(`${failed.length} sipariş başarısız:\n${failMsg}`, { duration: 8000 });
          console.error('[Reorder] failed orders:', failed);
        }
        setSelected({});
        fetchList();
      } else {
        toast.error(r.data?.message || 'Hata');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Bilinmeyen hata';
      toast.error('Toplu sipariş hatası: ' + msg, { duration: 8000 });
      console.error('[Reorder] bulk PO error:', e.response?.data, e);
    }
  };

  return (
    <div className="stocks-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ListPageHeader title="Sipariş Önerileri" unit="ürün" count={filtered.length}
        onRefresh={fetchList} refreshing={loading}
        actions={
          <>
            {selectedIds.length > 0 && (
              <Button size="sm" onClick={createBulkPOs}>
                📋 {selectedIds.length} kalem · {fmtAmount(totalSuggested)} ₺ → Toplu Sipariş
              </Button>
            )}
          </>
        }
      />

      <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
        {/* Filtre rozetleri */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { k: 'all',      l: 'Tümü',     c: 'var(--text-secondary)' },
            { k: 'out',      l: 'Tükenen',  c: '#dc2626' },
            { k: 'critical', l: 'Kritik',   c: '#f59e0b' },
            { k: 'reorder',  l: 'Sipariş Et', c: '#3b82f6' },
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              style={{
                padding: '6px 14px', fontSize: 'var(--text-sm)', fontWeight: 600,
                border: '1px solid ' + (filter === f.k ? f.c : 'var(--border-input)'),
                background: filter === f.k ? f.c + '22' : 'var(--bg-card)',
                color: filter === f.k ? f.c : 'var(--text-secondary)',
                borderRadius: 'var(--r-full)', cursor: 'pointer',
              }}>{f.l} ({counts[f.k]})</button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 20, color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
        ) : !filtered.length ? (
          <EmptyState icon="✅" title="Sipariş önerisi yok" description="Tüm stoklar yeterli seviyede. Min stok altına düşen ürün yok." />
        ) : (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            <table style={{ width: '100%', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-secondary)' }}>
                  <th style={{ ...S.th, width: 32 }}><input type="checkbox" onChange={e => {
                    const all = e.target.checked;
                    const newSel = {};
                    filtered.forEach(it => { newSel[it.id] = all; });
                    setSelected(newSel);
                  }} /></th>
                  <th style={S.th}>Durum</th>
                  <th style={S.th}>Ürün</th>
                  <th style={S.th}>Kategori</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Mevcut</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Min / Sipariş Noktası</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Öneri</th>
                  <th style={S.th}>Son Tedarikçi</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Son Fiyat</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Tahmini</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => {
                  const st = STATE_CONFIG[it.state] || STATE_CONFIG.reorder;
                  const lineTotal = (parseFloat(it.suggested_qty) || 0) * (parseFloat(it.last_unit_price) || 0);
                  return (
                    <tr key={it.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                      <td style={S.td}><input type="checkbox" checked={!!selected[it.id]} onChange={() => toggleSelect(it.id)} /></td>
                      <td style={S.td}>
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--r-full)', background: st.bg, color: st.color }}>{st.label}</span>
                      </td>
                      <td style={S.td}>
                        <strong>{it.name}</strong>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{it.code}{it.barcode ? ' · ' + it.barcode : ''}</div>
                      </td>
                      <td style={S.td}>{it.category_name || '—'}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: it.on_hand <= 0 ? 'var(--danger)' : (it.on_hand < it.min_stock ? 'var(--warning)' : 'var(--text-primary)'), fontWeight: 600 }}>
                        {fmtQty(it.on_hand)}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                        Min: {fmtQty(it.min_stock)}
                        {it.reorder_point > 0 && <div>Sip.Nok: {fmtQty(it.reorder_point)}</div>}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: 'var(--accent-primary)' }}>
                        {fmtQty(it.suggested_qty)}
                      </td>
                      <td style={S.td}>
                        {it.last_vendor_title ? (
                          <>
                            <strong>{it.last_vendor_title}</strong>
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                              {it.last_purchase_date ? new Date(it.last_purchase_date).toLocaleDateString('tr-TR') : ''}
                            </div>
                          </>
                        ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>
                        {it.last_unit_price ? fmtAmount(it.last_unit_price) + ' ₺' : '—'}
                      </td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>
                        {lineTotal > 0 ? fmtAmount(lineTotal) + ' ₺' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 14, padding: 12, background: 'var(--accent-glow)', border: '1px solid var(--border-primary)', borderRadius: 'var(--r-md)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
          <strong>💡 Nasıl çalışır?</strong> Stok kartında <strong>Minimum Stok</strong> ve <strong>Yeniden Sipariş Noktası</strong> alanlarını doldurun.
          Mevcut stok bu seviyenin altına düştüğünde burada görünür. <strong>Öneri miktarı:</strong> <strong>Maksimum Stok</strong> tanımlıysa <em>(Maks − Mevcut)</em>, yoksa <em>(Min × 2 − Mevcut)</em>. <strong>Son tedarikçi</strong> ve <strong>son fiyat</strong> önceki alış faturalarından gelir.
        </div>
      </div>
    </div>
  );
}

const S = {
  th: { padding: '10px 12px', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '10px 12px', verticalAlign: 'top' },
};
